/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-07-23
 */

// Add third-party dependencies.
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Add local dependencies.
import { User, authApi } from './authApi';

// Define the auth state interface for session-based authentication
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  csrfToken: string | null;
  sessionExpiry: number | null;
  isInitialized: boolean;
  sessionCheckAttempts: number;
}

// Persisted auth state holds only the boolean + expiry needed to gate the
// initial render. The full user object is XSS-readable in localStorage and
// must be re-fetched from auth/session/status/ on mount instead.
const loadPersistedState = (): Partial<AuthState> => {
  try {
    const persistedAuth = localStorage.getItem('tools-dance-auth-state');
    if (persistedAuth) {
      const parsed = JSON.parse(persistedAuth);
      if (parsed.sessionExpiry && Date.now() < parsed.sessionExpiry) {
        return {
          isAuthenticated: parsed.isAuthenticated,
          sessionExpiry: parsed.sessionExpiry,
        };
      }
    }
  } catch (error) {
    console.warn('Failed to load persisted auth state:', error);
  }
  return {};
};

const persistAuthState = (state: AuthState) => {
  try {
    const stateToPersist = {
      isAuthenticated: state.isAuthenticated,
      sessionExpiry: state.sessionExpiry,
    };
    localStorage.setItem(
      'tools-dance-auth-state',
      JSON.stringify(stateToPersist)
    );
  } catch (error) {
    console.warn('Failed to persist auth state:', error);
  }
};

// Define the initial state with persistence
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  csrfToken: null,
  sessionExpiry: null,
  isInitialized: false,
  sessionCheckAttempts: 0,
  ...loadPersistedState(),
};

// Create the auth slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ user: User; csrfToken?: string }>
    ) => {
      /* eslint-disable no-param-reassign */
      state.user = action.payload.user;
      state.isAuthenticated = true;
      state.isInitialized = true;
      if (action.payload.csrfToken) {
        state.csrfToken = action.payload.csrfToken;
      }
      persistAuthState(state);
      /* eslint-enable no-param-reassign */
    },
    logout: state => {
      /* eslint-disable no-param-reassign */
      state.user = null;
      state.isAuthenticated = false;
      state.csrfToken = null;
      state.sessionExpiry = null;
      state.isInitialized = true;
      state.sessionCheckAttempts = 0;
      // Clear persisted state
      localStorage.removeItem('tools-dance-auth-state');
      /* eslint-enable no-param-reassign */
    },
    updateUser: (state, action: PayloadAction<User>) => {
      /* eslint-disable no-param-reassign */
      state.user = action.payload;
      persistAuthState(state);
      /* eslint-enable no-param-reassign */
    },
    updateCsrfToken: (state, action: PayloadAction<string>) => {
      /* eslint-disable no-param-reassign */
      state.csrfToken = action.payload;
      /* eslint-enable no-param-reassign */
    },
    setSessionExpiry: (state, action: PayloadAction<number>) => {
      /* eslint-disable no-param-reassign */
      state.sessionExpiry = action.payload;
      persistAuthState(state);
      /* eslint-enable no-param-reassign */
    },
    setInitialized: (state, action: PayloadAction<boolean>) => {
      /* eslint-disable no-param-reassign */
      state.isInitialized = action.payload;
      /* eslint-enable no-param-reassign */
    },
    incrementSessionCheckAttempts: state => {
      /* eslint-disable no-param-reassign */
      state.sessionCheckAttempts += 1;
      /* eslint-enable no-param-reassign */
    },
    resetSessionCheckAttempts: state => {
      /* eslint-disable no-param-reassign */
      state.sessionCheckAttempts = 0;
      /* eslint-enable no-param-reassign */
    },
  },
  extraReducers: builder => {
    builder
      // Handle sessionStatus query fulfilled
      .addMatcher(
        authApi.endpoints.sessionStatus.matchFulfilled,
        (state, action) => {
          if (action.payload.authenticated && action.payload.user) {
            /* eslint-disable no-param-reassign */
            state.user = action.payload.user;
            state.isAuthenticated = true;
            state.isInitialized = true;
            if (action.payload.csrf_token) {
              state.csrfToken = action.payload.csrf_token;
            }
            persistAuthState(state);
            /* eslint-enable no-param-reassign */
          } else {
            /* eslint-disable no-param-reassign */
            state.user = null;
            state.isAuthenticated = false;
            state.isInitialized = true;
            /* eslint-enable no-param-reassign */
          }
        }
      )
      // Handle updateProfile mutation fulfilled
      .addMatcher(
        authApi.endpoints.updateProfile.matchFulfilled,
        (state, action) => {
          if (action.payload.user) {
            /* eslint-disable no-param-reassign */
            state.user = action.payload.user;
            persistAuthState(state);
            /* eslint-enable no-param-reassign */
          }
        }
      )
      // Handle login fulfilled
      .addMatcher(
        authApi.endpoints.sessionLogin.matchFulfilled,
        (state, action) => {
          /* eslint-disable no-param-reassign */
          state.user = action.payload.user;
          state.isAuthenticated = true;
          state.isInitialized = true;
          if (action.payload.csrf_token) {
            state.csrfToken = action.payload.csrf_token;
          }
          persistAuthState(state);
          /* eslint-enable no-param-reassign */
        }
      )
      // Handle logout fulfilled
      .addMatcher(authApi.endpoints.sessionLogout.matchFulfilled, state => {
        /* eslint-disable no-param-reassign */
        state.user = null;
        state.isAuthenticated = false;
        state.csrfToken = null;
        state.sessionExpiry = null;
        state.isInitialized = true;
        state.sessionCheckAttempts = 0;
        localStorage.removeItem('tools-dance-auth-state');
        /* eslint-enable no-param-reassign */
      });
  },
});

// Export actions
export const {
  setCredentials,
  logout,
  updateUser,
  updateCsrfToken,
  setSessionExpiry,
  setInitialized,
  incrementSessionCheckAttempts,
  resetSessionCheckAttempts,
} = authSlice.actions;

// Export reducer
export default authSlice.reducer;

// Export selectors
export const selectCurrentUser = (state: { auth: AuthState }) =>
  state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
export const selectCsrfToken = (state: { auth: AuthState }) =>
  state.auth.csrfToken;
export const selectSessionExpiry = (state: { auth: AuthState }) =>
  state.auth.sessionExpiry;
export const selectIsInitialized = (state: { auth: AuthState }) =>
  state.auth.isInitialized;
export const selectSessionCheckAttempts = (state: { auth: AuthState }) =>
  state.auth.sessionCheckAttempts;

// Device preferences that survive sign-out. Every other key can hold the
// signed-out user's data (chat id, studio ids, QuickBooks upload, ...).
const DEVICE_PREFERENCE_KEYS = [
  'tools-dance-theme-mode',
  'tools-dance-language',
  'scheduleColorCodeMode',
  'pwa-install-dismissed',
];

// Remove the signed-out user's data from browser storage
export const clearUserStorage = () => {
  try {
    Object.keys(localStorage)
      .filter(key => !DEVICE_PREFERENCE_KEYS.includes(key))
      .forEach(key => localStorage.removeItem(key));
    sessionStorage.clear();
  } catch {
    // Storage can be unavailable (e.g. some private browsing modes)
  }
};

// Marks this tab as just signed out, so its next sign-in skips the return
// path (Back can put the previous user's page there). One tab only (in
// sessionStorage); it holds no user data.
const SIGNED_OUT_KEY = 'tools-dance-signed-out';

export const markSignedOut = () => {
  try {
    sessionStorage.setItem(SIGNED_OUT_KEY, '1');
  } catch {
    // Storage unavailable: the next sign-in keeps its return path
  }
};

// True once after a sign-out in this tab; removes the marker
export const takeSignedOutMarker = (): boolean => {
  try {
    const signedOut = sessionStorage.getItem(SIGNED_OUT_KEY) !== null;
    sessionStorage.removeItem(SIGNED_OUT_KEY);
    return signedOut;
  } catch {
    return false;
  }
};
